import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { pettyCashAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Progress } from '../components/ui/progress';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import {
  Coins, Plus, Receipt, Wallet, TrendingUp, AlertTriangle, CheckCircle, XCircle,
  Calendar, DollarSign, Settings, BarChart3, RefreshCw, Clock, Sparkles,
  ArrowUpRight, ArrowDownRight, FileText, Upload, ChevronRight, Loader2
} from 'lucide-react';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount || 0);
};

const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  return new Date(dateStr).toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
};

const statusColors = {
  draft: 'bg-gray-100 text-gray-800',
  pending_approval: 'bg-yellow-100 text-yellow-800',
  approved: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
  reimbursed: 'bg-blue-100 text-blue-800',
  pending: 'bg-yellow-100 text-yellow-800',
  disbursed: 'bg-green-100 text-green-800',
};

const statusLabels = {
  draft: 'Draft',
  pending_approval: 'Pending Approval',
  approved: 'Approved',
  rejected: 'Rejected',
  reimbursed: 'Reimbursed',
  pending: 'Pending',
  disbursed: 'Disbursed',
};

export default function PettyCashPage() {
  const { user } = useAuth();
  const isAdmin = ['super_admin', 'admin', 'hr', 'accounts'].includes(user?.role);
  
  // State
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [categories, setCategories] = useState([]);
  const [paymentModes, setPaymentModes] = useState({});
  const [dashboard, setDashboard] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [cashRequests, setCashRequests] = useState([]);
  const [reconciliation, setReconciliation] = useState(null);
  const [aiInsights, setAiInsights] = useState(null);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [period, setPeriod] = useState('month');
  
  // Dialog states
  const [showExpenseDialog, setShowExpenseDialog] = useState(false);
  const [showCashRequestDialog, setShowCashRequestDialog] = useState(false);
  const [showCategoryDialog, setShowCategoryDialog] = useState(false);
  const [showConfigDialog, setShowConfigDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [expandedApproval, setExpandedApproval] = useState(null); // Track which item has expanded approval UI
  const [approvalRemarks, setApprovalRemarks] = useState(''); // Remarks for approve/reject
  
  // Form states
  const [expenseForm, setExpenseForm] = useState({
    category_id: '',
    description: '',
    amount: '',
    expense_date: new Date().toISOString().split('T')[0],
    payment_mode: 'director_upi',
    vendor_name: '',
    invoice_number: '',
    notes: '',
    receipt_urls: []
  });
  
  const [cashRequestForm, setCashRequestForm] = useState({
    amount: '',
    purpose: '',
    expected_usage_date: new Date().toISOString().split('T')[0],
    category_id: '',
    notes: ''
  });
  
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    icon: '📋',
    description: '',
    spending_limit: '',
    is_active: true
  });
  
  const [configForm, setConfigForm] = useState({
    approval_threshold: 500,
    monthly_budget: 50000,
    require_receipt_above: 100,
    auto_approve_below_threshold: true
  });

  // Fetch data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [configRes, dashboardRes, expensesRes, requestsRes] = await Promise.all([
        pettyCashAPI.getConfig(),
        pettyCashAPI.getDashboard(period),
        pettyCashAPI.getExpenses({ limit: 50 }),
        pettyCashAPI.getCashRequests({ limit: 50 })
      ]);
      
      setConfig(configRes.data.config);
      setCategories(configRes.data.categories || []);
      setPaymentModes(configRes.data.payment_modes || {});
      setDashboard(dashboardRes.data);
      setExpenses(expensesRes.data.expenses || []);
      setCashRequests(requestsRes.data.requests || []);
      
      // Set config form
      if (configRes.data.config) {
        setConfigForm({
          approval_threshold: configRes.data.config.approval_threshold || 500,
          monthly_budget: configRes.data.config.monthly_budget || 50000,
          require_receipt_above: configRes.data.config.require_receipt_above || 100,
          auto_approve_below_threshold: configRes.data.config.auto_approve_below_threshold ?? true
        });
      }
    } catch (err) {
      toast.error('Failed to load petty cash data');
    } finally {
      setLoading(false);
    }
  }, [period]);

  const fetchAIInsights = async () => {
    try {
      const res = await pettyCashAPI.getAIInsights();
      setAiInsights(res.data);
    } catch (err) {
      console.error('Failed to fetch AI insights:', err);
    }
  };

  const fetchReconciliation = async (reconcPeriod) => {
    try {
      const res = await pettyCashAPI.getReconciliation(reconcPeriod);
      setReconciliation(res.data);
    } catch (err) {
      console.error('Failed to fetch reconciliation:', err);
    }
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (activeTab === 'insights') {
      fetchAIInsights();
    } else if (activeTab === 'reconciliation') {
      fetchReconciliation('week');
    }
  }, [activeTab]);

  // Handlers
  const handleCreateExpense = async () => {
    if (!expenseForm.category_id || !expenseForm.description || !expenseForm.amount) {
      toast.error('Please fill all required fields');
      return;
    }
    
    setSubmitting(true);
    try {
      await pettyCashAPI.createExpense({
        ...expenseForm,
        amount: parseFloat(expenseForm.amount)
      });
      toast.success('Expense submitted successfully');
      setShowExpenseDialog(false);
      setExpenseForm({
        category_id: '',
        description: '',
        amount: '',
        expense_date: new Date().toISOString().split('T')[0],
        payment_mode: 'director_upi',
        vendor_name: '',
        invoice_number: '',
        notes: '',
        receipt_urls: []
      });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create expense');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateCashRequest = async () => {
    if (!cashRequestForm.amount || !cashRequestForm.purpose) {
      toast.error('Please fill all required fields');
      return;
    }
    
    setSubmitting(true);
    try {
      await pettyCashAPI.createCashRequest({
        ...cashRequestForm,
        amount: parseFloat(cashRequestForm.amount)
      });
      toast.success('Cash request submitted successfully');
      setShowCashRequestDialog(false);
      setCashRequestForm({
        amount: '',
        purpose: '',
        expected_usage_date: new Date().toISOString().split('T')[0],
        category_id: '',
        notes: ''
      });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create cash request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleApprove = async (type, id, remarks = '') => {
    setSubmitting(true);
    try {
      if (type === 'expense') {
        await pettyCashAPI.approveExpense(id, remarks);
      } else {
        await pettyCashAPI.approveCashRequest(id, 'director_upi', '', remarks);
      }
      toast.success(`${type === 'expense' ? 'Expense' : 'Cash request'} approved`);
      setExpandedApproval(null);
      setApprovalRemarks('');
      setSelectedItem(null);
      fetchData();
    } catch (err) {
      toast.error('Failed to approve');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async (type, id, reason) => {
    setSubmitting(true);
    try {
      if (type === 'expense') {
        await pettyCashAPI.rejectExpense(id, reason);
      } else {
        await pettyCashAPI.rejectCashRequest(id, reason);
      }
      toast.success(`${type === 'expense' ? 'Expense' : 'Cash request'} rejected`);
      setExpandedApproval(null);
      setApprovalRemarks('');
      setSelectedItem(null);
      fetchData();
    } catch (err) {
      toast.error('Failed to reject');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleApprovalPanel = (itemId, type, item) => {
    if (expandedApproval === itemId) {
      setExpandedApproval(null);
      setApprovalRemarks('');
      setSelectedItem(null);
    } else {
      setExpandedApproval(itemId);
      setApprovalRemarks('');
      setSelectedItem({ type, ...item });
    }
  };

  const handleCreateCategory = async () => {
    if (!categoryForm.name) {
      toast.error('Category name is required');
      return;
    }
    
    setSubmitting(true);
    try {
      await pettyCashAPI.createCategory({
        ...categoryForm,
        spending_limit: categoryForm.spending_limit ? parseFloat(categoryForm.spending_limit) : null
      });
      toast.success('Category created');
      setShowCategoryDialog(false);
      setCategoryForm({ name: '', icon: '📋', description: '', spending_limit: '', is_active: true });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create category');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateConfig = async () => {
    setSubmitting(true);
    try {
      await pettyCashAPI.updateConfig(configForm);
      toast.success('Configuration updated');
      setShowConfigDialog(false);
      fetchData();
    } catch (err) {
      toast.error('Failed to update configuration');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const budgetUtilization = dashboard?.summary?.budget_utilization || 0;
  const pendingApprovals = dashboard?.summary?.pending_approvals || 0;
  const pendingRequests = dashboard?.summary?.pending_cash_requests || 0;

  return (
    <div className="space-y-6" data-testid="petty-cash-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Coins className="h-7 w-7 text-primary" />
            Petty Cash Management
          </h1>
          <p className="text-muted-foreground mt-1">
            Track and manage daily office expenses
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setShowCashRequestDialog(true)} data-testid="request-cash-btn">
            <Wallet className="h-4 w-4 mr-2" />
            Request Cash
          </Button>
          <Button onClick={() => setShowExpenseDialog(true)} data-testid="add-expense-btn">
            <Plus className="h-4 w-4 mr-2" />
            Add Expense
          </Button>
          {isAdmin && (
            <Button variant="outline" size="icon" onClick={() => setShowConfigDialog(true)} data-testid="config-btn">
              <Settings className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Budget Alert Banner */}
      {budgetUtilization > 75 && (
        <div className={`p-4 rounded-lg flex items-center gap-3 ${budgetUtilization >= 90 ? 'bg-red-50 border border-red-200' : 'bg-yellow-50 border border-yellow-200'}`}>
          <AlertTriangle className={`h-5 w-5 ${budgetUtilization >= 90 ? 'text-red-600' : 'text-yellow-600'}`} />
          <div className="flex-1">
            <p className={`font-medium ${budgetUtilization >= 90 ? 'text-red-800' : 'text-yellow-800'}`}>
              {budgetUtilization >= 100 ? 'Budget Exceeded!' : budgetUtilization >= 90 ? 'Budget Critical!' : 'Budget Warning'}
            </p>
            <p className={`text-sm ${budgetUtilization >= 90 ? 'text-red-600' : 'text-yellow-600'}`}>
              {budgetUtilization.toFixed(1)}% of monthly budget utilized
            </p>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Spent</p>
                <p className="text-2xl font-bold">{formatCurrency(dashboard?.summary?.total_spent)}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {dashboard?.summary?.expense_count || 0} transactions
                </p>
              </div>
              <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                <DollarSign className="h-6 w-6 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Monthly Budget</p>
                <p className="text-2xl font-bold">{formatCurrency(dashboard?.summary?.monthly_budget)}</p>
                <div className="mt-2 space-y-1">
                  <Progress value={Math.min(budgetUtilization, 100)} className="h-2" />
                  <p className="text-xs text-muted-foreground">{budgetUtilization.toFixed(1)}% used</p>
                </div>
              </div>
              <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
                <TrendingUp className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Pending Approvals</p>
                <p className="text-2xl font-bold">{pendingApprovals}</p>
                <p className="text-xs text-muted-foreground mt-1">expenses to review</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-yellow-100 flex items-center justify-center">
                <Clock className="h-6 w-6 text-yellow-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Cash Requests</p>
                <p className="text-2xl font-bold">{pendingRequests}</p>
                <p className="text-xs text-muted-foreground mt-1">pending disbursement</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
                <Wallet className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="dashboard" data-testid="tab-dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="expenses" data-testid="tab-expenses">Expenses</TabsTrigger>
          <TabsTrigger value="requests" data-testid="tab-requests">Cash Requests</TabsTrigger>
          {isAdmin && <TabsTrigger value="reconciliation" data-testid="tab-reconciliation">Reconciliation</TabsTrigger>}
          {isAdmin && <TabsTrigger value="insights" data-testid="tab-insights">AI Insights</TabsTrigger>}
          {isAdmin && <TabsTrigger value="categories" data-testid="tab-categories">Categories</TabsTrigger>}
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* By Category */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Spending by Category</CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[300px]">
                  {dashboard?.by_category?.length > 0 ? (
                    <div className="space-y-3">
                      {dashboard.by_category.map((cat, idx) => (
                        <div key={idx} className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50">
                          <span className="text-2xl">{cat.category_icon}</span>
                          <div className="flex-1">
                            <p className="font-medium">{cat.category_name}</p>
                            <p className="text-sm text-muted-foreground">{cat.count} transactions</p>
                          </div>
                          <p className="font-semibold">{formatCurrency(cat.amount)}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted-foreground text-center py-8">No expenses yet</p>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>

            {/* By Payment Mode */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Spending by Payment Mode</CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[300px]">
                  {dashboard?.by_payment_mode?.length > 0 ? (
                    <div className="space-y-3">
                      {dashboard.by_payment_mode.map((mode, idx) => (
                        <div key={idx} className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50">
                          <span className="text-2xl">{mode.payment_mode_icon}</span>
                          <div className="flex-1">
                            <p className="font-medium">{mode.payment_mode_name}</p>
                            <p className="text-sm text-muted-foreground">{mode.count} transactions</p>
                          </div>
                          <p className="font-semibold">{formatCurrency(mode.amount)}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted-foreground text-center py-8">No payments yet</p>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
          </div>

          {/* Recent Expenses */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">Recent Expenses</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setActiveTab('expenses')}>
                View All <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </CardHeader>
            <CardContent>
              {dashboard?.recent_expenses?.length > 0 ? (
                <div className="space-y-2">
                  {dashboard.recent_expenses.slice(0, 5).map((exp, idx) => (
                    <div key={idx} className="flex items-center gap-3 p-3 border rounded-lg">
                      <span className="text-xl">{exp.category_icon || '📋'}</span>
                      <div className="flex-1">
                        <p className="font-medium">{exp.description}</p>
                        <p className="text-sm text-muted-foreground">{formatDate(exp.expense_date)}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold">{formatCurrency(exp.amount)}</p>
                        <Badge className={statusColors[exp.status]}>
                          {statusLabels[exp.status]}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground text-center py-8">No recent expenses</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Expenses Tab */}
        <TabsContent value="expenses" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>All Expenses</CardTitle>
                <Button onClick={() => setShowExpenseDialog(true)}>
                  <Plus className="h-4 w-4 mr-2" /> Add Expense
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[500px]">
                {expenses.length > 0 ? (
                  <div className="space-y-2">
                    {expenses.map((exp) => (
                      <div key={exp.id} className="border rounded-lg overflow-hidden">
                        <div className="flex items-center gap-3 p-3 hover:bg-muted/50">
                          <span className="text-xl">{exp.category_icon || '📋'}</span>
                          <div className="flex-1">
                            <p className="font-medium">{exp.description}</p>
                            <p className="text-sm text-muted-foreground">
                              {exp.category_name} • {formatDate(exp.expense_date)} • {paymentModes[exp.payment_mode]?.name || exp.payment_mode}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold">{formatCurrency(exp.amount)}</p>
                            <Badge className={statusColors[exp.status]}>
                              {statusLabels[exp.status]}
                            </Badge>
                          </div>
                          {isAdmin && exp.status === 'pending_approval' && (
                            <div className="flex gap-1">
                              <Button
                                size="sm"
                                variant="default"
                                className="bg-green-600 hover:bg-green-700 text-white"
                                onClick={() => handleApprove('expense', exp.id)}
                                disabled={submitting}
                                data-testid={`approve-expense-${exp.id}`}
                              >
                                <CheckCircle className="h-4 w-4 mr-1" />
                                Approve
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => toggleApprovalPanel(exp.id, 'expense', exp)}
                                data-testid={`reject-expense-${exp.id}`}
                              >
                                <XCircle className="h-4 w-4 mr-1" />
                                Reject
                              </Button>
                            </div>
                          )}
                        </div>
                        {/* Inline Rejection Panel */}
                        {expandedApproval === exp.id && selectedItem?.type === 'expense' && (
                          <div className="px-4 pb-4 pt-2 bg-red-50 border-t border-red-200">
                            <div className="flex items-center gap-3">
                              <div className="flex-1">
                                <Input
                                  placeholder="Rejection reason (optional)"
                                  value={approvalRemarks}
                                  onChange={(e) => setApprovalRemarks(e.target.value)}
                                  className="bg-white"
                                  data-testid={`reject-reason-${exp.id}`}
                                />
                              </div>
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => handleReject('expense', exp.id, approvalRemarks)}
                                disabled={submitting}
                                data-testid={`confirm-reject-${exp.id}`}
                              >
                                {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                                Confirm Reject
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setExpandedApproval(null);
                                  setApprovalRemarks('');
                                }}
                              >
                                Cancel
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-8">No expenses found</p>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Cash Requests Tab */}
        <TabsContent value="requests" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Cash Requests</CardTitle>
                <Button onClick={() => setShowCashRequestDialog(true)}>
                  <Plus className="h-4 w-4 mr-2" /> Request Cash
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[500px]">
                {cashRequests.length > 0 ? (
                  <div className="space-y-2">
                    {cashRequests.map((req) => (
                      <div key={req.id} className="border rounded-lg overflow-hidden">
                        <div className="flex items-center gap-3 p-3 hover:bg-muted/50">
                          <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                            <Wallet className="h-5 w-5 text-primary" />
                          </div>
                          <div className="flex-1">
                            <p className="font-medium">{req.purpose}</p>
                            <p className="text-sm text-muted-foreground">
                              {req.requested_by_name} • Expected: {formatDate(req.expected_usage_date)}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold">{formatCurrency(req.amount)}</p>
                            <Badge className={statusColors[req.status]}>
                              {statusLabels[req.status]}
                            </Badge>
                          </div>
                          {isAdmin && req.status === 'pending' && (
                            <div className="flex gap-1">
                              <Button
                                size="sm"
                                variant="default"
                                className="bg-green-600 hover:bg-green-700 text-white"
                                onClick={() => handleApprove('request', req.id)}
                                disabled={submitting}
                                data-testid={`approve-request-${req.id}`}
                              >
                                <CheckCircle className="h-4 w-4 mr-1" />
                                Approve
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => toggleApprovalPanel(req.id, 'request', req)}
                                data-testid={`reject-request-${req.id}`}
                              >
                                <XCircle className="h-4 w-4 mr-1" />
                                Reject
                              </Button>
                            </div>
                          )}
                        </div>
                        {/* Inline Rejection Panel */}
                        {expandedApproval === req.id && selectedItem?.type === 'request' && (
                          <div className="px-4 pb-4 pt-2 bg-red-50 border-t border-red-200">
                            <div className="flex items-center gap-3">
                              <div className="flex-1">
                                <Input
                                  placeholder="Rejection reason (optional)"
                                  value={approvalRemarks}
                                  onChange={(e) => setApprovalRemarks(e.target.value)}
                                  className="bg-white"
                                  data-testid={`reject-reason-${req.id}`}
                                />
                              </div>
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => handleReject('request', req.id, approvalRemarks)}
                                disabled={submitting}
                                data-testid={`confirm-reject-${req.id}`}
                              >
                                {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                                Confirm Reject
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setExpandedApproval(null);
                                  setApprovalRemarks('');
                                }}
                              >
                                Cancel
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-8">No cash requests</p>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Reconciliation Tab */}
        {isAdmin && (
          <TabsContent value="reconciliation" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Reconciliation</CardTitle>
                    <CardDescription>Weekly and monthly expense reconciliation</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant={reconciliation?.period === 'week' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => fetchReconciliation('week')}
                    >
                      Weekly
                    </Button>
                    <Button
                      variant={reconciliation?.period === 'month' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => fetchReconciliation('month')}
                    >
                      Monthly
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {reconciliation ? (
                  <div className="space-y-6">
                    {/* Summary */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="p-4 bg-green-50 rounded-lg">
                        <p className="text-sm text-green-600">Total Expenses</p>
                        <p className="text-2xl font-bold text-green-700">
                          {formatCurrency(reconciliation.summary?.total_expenses)}
                        </p>
                      </div>
                      <div className="p-4 bg-blue-50 rounded-lg">
                        <p className="text-sm text-blue-600">Cash Disbursed</p>
                        <p className="text-2xl font-bold text-blue-700">
                          {formatCurrency(reconciliation.summary?.total_cash_disbursed)}
                        </p>
                      </div>
                      <div className="p-4 bg-purple-50 rounded-lg">
                        <p className="text-sm text-purple-600">Net Cash Flow</p>
                        <p className="text-2xl font-bold text-purple-700">
                          {formatCurrency(reconciliation.summary?.net_cash_flow)}
                        </p>
                      </div>
                    </div>

                    {/* Daily Breakdown */}
                    {reconciliation.daily_breakdown?.length > 0 && (
                      <div>
                        <h3 className="font-semibold mb-3">Daily Breakdown</h3>
                        <div className="space-y-2">
                          {reconciliation.daily_breakdown.map((day, idx) => (
                            <div key={idx} className="flex items-center justify-between p-3 border rounded-lg">
                              <div>
                                <p className="font-medium">{formatDate(day.date)}</p>
                                <p className="text-sm text-muted-foreground">{day.count} transactions</p>
                              </div>
                              <p className="font-semibold">{formatCurrency(day.amount)}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* AI Insights Tab */}
        {isAdmin && (
          <TabsContent value="insights" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Sparkles className="h-5 w-5 text-yellow-500" />
                      AI Insights
                    </CardTitle>
                    <CardDescription>AI-powered analysis of your petty cash expenses</CardDescription>
                  </div>
                  <Button variant="outline" onClick={fetchAIInsights}>
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Refresh
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {aiInsights ? (
                  <div className="space-y-6">
                    {/* Budget Status */}
                    {aiInsights.budget_status && (
                      <div className={`p-4 rounded-lg border ${
                        aiInsights.budget_status.status === 'healthy' ? 'bg-green-50 border-green-200' :
                        aiInsights.budget_status.status === 'warning' ? 'bg-yellow-50 border-yellow-200' :
                        'bg-red-50 border-red-200'
                      }`}>
                        <div className="flex items-center gap-2 mb-2">
                          <BarChart3 className="h-5 w-5" />
                          <h3 className="font-semibold">Budget Status: {aiInsights.budget_status.status.toUpperCase()}</h3>
                        </div>
                        <p className="text-sm">
                          {aiInsights.budget_status.utilization_percent}% utilized • 
                          {formatCurrency(aiInsights.budget_status.remaining)} remaining
                        </p>
                      </div>
                    )}

                    {/* Alerts */}
                    {aiInsights.alerts?.length > 0 && (
                      <div className="space-y-2">
                        <h3 className="font-semibold flex items-center gap-2">
                          <AlertTriangle className="h-5 w-5 text-yellow-500" />
                          Alerts
                        </h3>
                        {aiInsights.alerts.map((alert, idx) => (
                          <div key={idx} className={`p-3 rounded-lg border ${
                            alert.severity === 'high' ? 'bg-red-50 border-red-200' : 'bg-yellow-50 border-yellow-200'
                          }`}>
                            <p className="font-medium">{alert.title}</p>
                            <p className="text-sm">{alert.message}</p>
                            {alert.action && (
                              <p className="text-sm text-muted-foreground mt-1">Action: {alert.action}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* AI Analysis */}
                    {aiInsights.ai_analysis && (
                      <div className="p-4 bg-gradient-to-br from-purple-50 to-blue-50 rounded-lg border">
                        <h3 className="font-semibold mb-3 flex items-center gap-2">
                          <Sparkles className="h-5 w-5 text-purple-500" />
                          AI Analysis
                        </h3>
                        <div className="prose prose-sm max-w-none whitespace-pre-wrap">
                          {aiInsights.ai_analysis}
                        </div>
                      </div>
                    )}

                    {/* Rule-based Insights */}
                    {aiInsights.insights?.length > 0 && (
                      <div className="space-y-2">
                        <h3 className="font-semibold">Insights</h3>
                        {aiInsights.insights.map((insight, idx) => (
                          <div key={idx} className={`p-3 rounded-lg border ${
                            insight.type === 'warning' ? 'bg-yellow-50 border-yellow-200' : 'bg-blue-50 border-blue-200'
                          }`}>
                            <Badge variant="outline" className="mb-1">{insight.category}</Badge>
                            <p className="text-sm">{insight.message}</p>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Anomalies */}
                    {aiInsights.anomalies?.length > 0 && (
                      <div className="space-y-2">
                        <h3 className="font-semibold flex items-center gap-2">
                          <AlertTriangle className="h-5 w-5 text-orange-500" />
                          Detected Anomalies
                        </h3>
                        {aiInsights.anomalies.map((anomaly, idx) => (
                          <div key={idx} className="p-3 bg-orange-50 rounded-lg border border-orange-200">
                            <p className="text-sm">{anomaly.reason}</p>
                            {anomaly.amount && (
                              <p className="text-xs text-muted-foreground mt-1">
                                Amount: {formatCurrency(anomaly.amount)} • Date: {formatDate(anomaly.date)}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center justify-center py-12">
                    <div className="text-center">
                      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground mx-auto mb-2" />
                      <p className="text-muted-foreground">Generating AI insights...</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* Categories Tab */}
        {isAdmin && (
          <TabsContent value="categories" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Expense Categories</CardTitle>
                  <Button onClick={() => setShowCategoryDialog(true)}>
                    <Plus className="h-4 w-4 mr-2" /> Add Category
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {categories.map((cat) => (
                    <div key={cat.id} className="p-4 border rounded-lg flex items-start gap-3">
                      <span className="text-2xl">{cat.icon}</span>
                      <div className="flex-1">
                        <p className="font-medium">{cat.name}</p>
                        {cat.description && (
                          <p className="text-sm text-muted-foreground">{cat.description}</p>
                        )}
                        {cat.spending_limit && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Limit: {formatCurrency(cat.spending_limit)}
                          </p>
                        )}
                      </div>
                      {!cat.is_default && (
                        <Badge variant="outline">Custom</Badge>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* Add Expense Dialog */}
      <Dialog open={showExpenseDialog} onOpenChange={setShowExpenseDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Expense</DialogTitle>
            <DialogDescription>Record a new petty cash expense</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Category *</Label>
                <Select
                  value={expenseForm.category_id}
                  onValueChange={(v) => setExpenseForm({ ...expenseForm, category_id: v })}
                >
                  <SelectTrigger data-testid="expense-category-select">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>
                        {cat.icon} {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Amount (₹) *</Label>
                <Input
                  type="number"
                  placeholder="0.00"
                  value={expenseForm.amount}
                  onChange={(e) => setExpenseForm({ ...expenseForm, amount: e.target.value })}
                  data-testid="expense-amount-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Description *</Label>
              <Input
                placeholder="Brief description of the expense"
                value={expenseForm.description}
                onChange={(e) => setExpenseForm({ ...expenseForm, description: e.target.value })}
                data-testid="expense-description-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Expense Date</Label>
                <Input
                  type="date"
                  value={expenseForm.expense_date}
                  onChange={(e) => setExpenseForm({ ...expenseForm, expense_date: e.target.value })}
                  data-testid="expense-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Payment Mode</Label>
                <Select
                  value={expenseForm.payment_mode}
                  onValueChange={(v) => setExpenseForm({ ...expenseForm, payment_mode: v })}
                >
                  <SelectTrigger data-testid="expense-payment-mode-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(paymentModes).map(([key, mode]) => (
                      <SelectItem key={key} value={key}>
                        {mode.icon} {mode.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Vendor Name</Label>
                <Input
                  placeholder="Optional"
                  value={expenseForm.vendor_name}
                  onChange={(e) => setExpenseForm({ ...expenseForm, vendor_name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Invoice/Bill #</Label>
                <Input
                  placeholder="Optional"
                  value={expenseForm.invoice_number}
                  onChange={(e) => setExpenseForm({ ...expenseForm, invoice_number: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                placeholder="Additional notes (optional)"
                value={expenseForm.notes}
                onChange={(e) => setExpenseForm({ ...expenseForm, notes: e.target.value })}
                rows={2}
              />
            </div>
            {config && expenseForm.amount && parseFloat(expenseForm.amount) > config.approval_threshold && (
              <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-200 text-sm">
                <AlertTriangle className="h-4 w-4 inline-block mr-2 text-yellow-600" />
                This expense exceeds the approval threshold of {formatCurrency(config.approval_threshold)} and will require approval.
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowExpenseDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateExpense} disabled={submitting} data-testid="submit-expense-btn">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Submit Expense
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cash Request Dialog */}
      <Dialog open={showCashRequestDialog} onOpenChange={setShowCashRequestDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Request Cash</DialogTitle>
            <DialogDescription>Submit a cash advance request</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Amount (₹) *</Label>
                <Input
                  type="number"
                  placeholder="0.00"
                  value={cashRequestForm.amount}
                  onChange={(e) => setCashRequestForm({ ...cashRequestForm, amount: e.target.value })}
                  data-testid="cash-request-amount-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Expected Usage Date</Label>
                <Input
                  type="date"
                  value={cashRequestForm.expected_usage_date}
                  onChange={(e) => setCashRequestForm({ ...cashRequestForm, expected_usage_date: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Purpose *</Label>
              <Input
                placeholder="Reason for cash request"
                value={cashRequestForm.purpose}
                onChange={(e) => setCashRequestForm({ ...cashRequestForm, purpose: e.target.value })}
                data-testid="cash-request-purpose-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Category (Optional)</Label>
              <Select
                value={cashRequestForm.category_id}
                onValueChange={(v) => setCashRequestForm({ ...cashRequestForm, category_id: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>
                      {cat.icon} {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                placeholder="Additional notes (optional)"
                value={cashRequestForm.notes}
                onChange={(e) => setCashRequestForm({ ...cashRequestForm, notes: e.target.value })}
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCashRequestDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateCashRequest} disabled={submitting} data-testid="submit-cash-request-btn">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Submit Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Category Dialog */}
      <Dialog open={showCategoryDialog} onOpenChange={setShowCategoryDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Category</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label>Icon</Label>
                <Input
                  value={categoryForm.icon}
                  onChange={(e) => setCategoryForm({ ...categoryForm, icon: e.target.value })}
                  maxLength={2}
                />
              </div>
              <div className="col-span-3 space-y-2">
                <Label>Name *</Label>
                <Input
                  placeholder="Category name"
                  value={categoryForm.name}
                  onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                placeholder="Brief description"
                value={categoryForm.description}
                onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Spending Limit (₹)</Label>
              <Input
                type="number"
                placeholder="Optional monthly limit"
                value={categoryForm.spending_limit}
                onChange={(e) => setCategoryForm({ ...categoryForm, spending_limit: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCategoryDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateCategory} disabled={submitting}>
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Create Category
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Config Dialog */}
      <Dialog open={showConfigDialog} onOpenChange={setShowConfigDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Petty Cash Configuration</DialogTitle>
            <DialogDescription>Configure approval thresholds and budget limits</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Approval Threshold (₹)</Label>
              <Input
                type="number"
                value={configForm.approval_threshold}
                onChange={(e) => setConfigForm({ ...configForm, approval_threshold: parseFloat(e.target.value) || 0 })}
              />
              <p className="text-xs text-muted-foreground">
                Expenses above this amount require approval
              </p>
            </div>
            <div className="space-y-2">
              <Label>Monthly Budget (₹)</Label>
              <Input
                type="number"
                value={configForm.monthly_budget}
                onChange={(e) => setConfigForm({ ...configForm, monthly_budget: parseFloat(e.target.value) || 0 })}
              />
            </div>
            <div className="space-y-2">
              <Label>Receipt Required Above (₹)</Label>
              <Input
                type="number"
                value={configForm.require_receipt_above}
                onChange={(e) => setConfigForm({ ...configForm, require_receipt_above: parseFloat(e.target.value) || 0 })}
              />
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="auto_approve"
                checked={configForm.auto_approve_below_threshold}
                onChange={(e) => setConfigForm({ ...configForm, auto_approve_below_threshold: e.target.checked })}
                className="rounded border-gray-300"
              />
              <Label htmlFor="auto_approve">Auto-approve expenses below threshold</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfigDialog(false)}>Cancel</Button>
            <Button onClick={handleUpdateConfig} disabled={submitting}>
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Save Configuration
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
